import { debounce } from "lodash-es";
import objectHash from "object-hash";
import React, { FC, useCallback, useEffect, useState } from "react";

import {
  AutocompleteInputChangeReason,
  Stack,
  Theme,
  useMediaQuery,
} from "@mui/material";

import Autocomplete from "../../../../components/styled/Autocomplete";
import StyledTextField from "../../../../components/styled/TextField";
import {
  getRouteFormProperty,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import {
  useGetClusterQuery,
  useGetSettingsQuery,
  useListCustomDomainsQuery,
} from "../../../../store/pomeriumApi";
import { validateFrom } from "../../../../validators/from";

type Props = {
  readonly: boolean;
};

function getFromProtocol(from: string | undefined): string {
  if (!from) {
    return "https://";
  }

  const idx = from.indexOf("://");
  if (idx < 0) {
    return "https://";
  }

  return from.substring(0, idx + 3);
}

const FromField: FC<Props> = ({ readonly }) => {
  const dispatch = useDispatch();
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg"),
  );
  const from = useSelector(getRouteFormProperty("from", "")) as string;
  const [protocol, setProtocol] = useState<string>(getFromProtocol(from));
  const [, fqdn] = from.split("://");
  const [inputValue, setInputValue] = useState<string>(fqdn || "");
  const [selectedValue, setSelectedValue] = useState<string>(fqdn || "");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [fqdns, setFqdns] = useState<string[]>([]);
  const [options, setOptions] = useState<string[]>([]);
  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );
  const { data: customDomains } = useListCustomDomainsQuery({
    organizationId,
    clusterId,
  });

  const { data: settings } = useGetSettingsQuery(
    {
      organizationId,
      clusterId,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );

  useEffect(() => {
    const opts = customDomains?.map((d) => d.domainName) || [];
    if (cluster?.fqdn) {
      opts.push(cluster.fqdn);
    }
    const [, port] = (settings?.address || "").split(":");
    if (port && port !== "443") {
      setFqdns(opts.map((d) => d + ":" + port));
    } else {
      setFqdns(opts);
    }
  }, [
    objectHash(customDomains || []),
    cluster?.fqdn,
    objectHash(settings || {}),
  ]);

  useEffect(() => {
    if (inputValue && protocol !== "ssh://") {
      const [prefix] = inputValue.split(".");
      setOptions(
        fqdns.map((d) => {
          return prefix + "." + d;
        }),
      );
    } else {
      setOptions([]);
    }
  }, [JSON.stringify(fqdns), inputValue]);

  useEffect(() => {
    const newProtocol = getFromProtocol(from);
    if (newProtocol !== protocol) {
      setProtocol(newProtocol);
    }
  }, [from]);

  const delayedSave = useCallback(
    debounce((val) => {
      setErrorMessage(validateFrom(protocol + val));
      dispatch(
        setRouteFormProperty({
          path: "from",
          value: protocol + val,
        }),
      );
    }, 150),
    [protocol],
  );

  const onInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason !== "reset" && event.type !== "blur") {
      setInputValue(value || "");
      delayedSave(value || "");
    }
  };

  const onBlur = (_event: React.SyntheticEvent, val: any): void => {
    setInputValue(val || "");
    setSelectedValue(val || "");
    dispatch(
      setRouteFormProperty({
        path: "from",
        value: protocol + val,
      }),
    );
    setErrorMessage(validateFrom(protocol + val));
  };

  return (
    <Stack
      direction={smallScreen ? "column" : "row"}
      gap={smallScreen ? 2 : 1}
      sx={{
        width: "100%",
        alignItems: "flex-start",
      }}
    >
      <StyledTextField
        fullWidth={smallScreen}
        value={protocol}
        label="Protocol"
        InputProps={{ readOnly: true }}
        sx={
          smallScreen ? {} : { flexShrink: 0, maxWidth: "8rem", width: "8rem" }
        }
      />
      <Autocomplete
        fullWidth
        id="fqdn"
        freeSolo
        options={options}
        inputValue={inputValue || ""}
        onInputChange={onInputChange}
        value={selectedValue || ""}
        onChange={onBlur}
        sx={smallScreen ? {} : { flex: 1 }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label="Host"
            error={!!errorMessage.length}
            helperText={errorMessage}
            disabled={readonly}
            inputProps={{
              ...params.inputProps,
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  onBlur(e, inputValue);
                }
              },
            }}
          />
        )}
      />
    </Stack>
  );
};

export default FromField;
